<script setup>
import { ref, computed, watch } from 'vue'
import { trackEvent } from '@/composables/useAnalytics'
import TeachingMethodsSelection from '@/Components/Forms/TeachingMethodsSelection.vue'
import PricingPlanSelector from '@/Components/Forms/PricingPlanSelector.vue'
import SelectionSummary from '@/Components/Forms/SelectionSummary.vue'
import ProTipsCard from '@/Components/Forms/ProTipsCard.vue'
import HelpSupportCard from '@/Components/Forms/HelpSupportCard.vue'
// Hidden for now
// import AdditionalServicesSection from '@/Components/Forms/AdditionalServicesSection.vue'
// import TestimonialsSection from '@/Components/Forms/TestimonialsSection.vue'
import NavigationButtons from '@/Components/Onboarding/NavigationButtons.vue'

const props = defineProps({
  teachingMethods: {
    type: Array,
    required: true
  },
  additionalServices: {
    type: Array,
    required: true
  },
  application: Object,
  slug: String
})

const emit = defineEmits(['next', 'back'])

const selectedMethods = ref([])
const selectedAdditionalServices = ref([])
const aboutMe = ref('')
const teachingMethodsRef = ref(null)
const loading = ref(false)
const errors = ref({})

// Computed for character count
const aboutMeCharCount = computed(() => aboutMe.value.length)
const aboutMeMinChars = 100
const aboutMeMaxChars = 2000

// Populate from application data
const populateFromApplication = (app) => {
  if (!app) return

  selectedMethods.value = app.teaching_method_ids || []
  selectedAdditionalServices.value = app.additional_services || []
  aboutMe.value = app.about_me || ''
}

// Watch for application prop changes
watch(() => props.application, (newApp) => {
  if (newApp) {
    populateFromApplication(newApp)
  }
}, { immediate: true })

// Determine the plan tiers based on selected methods
const selectedMethodNames = computed(() => {
  return props.teachingMethods
    .filter(m => selectedMethods.value.includes(m.id))
    .map(m => m.name.toLowerCase())
})

const hasBasicMethods = computed(() => {
  return selectedMethodNames.value.some(name =>
    name.includes('study material') || name.includes('recorded') || name.includes('video')
  )
})

const hasSilverMethods = computed(() => {
  return selectedMethodNames.value.some(name => name.includes('group'))
})

const hasGoldMethods = computed(() => {
  return selectedMethodNames.value.some(name =>
    name.includes('1:1') || name.includes('one-to-one') || name.includes('1-on-1')
  )
})

// Get list of all active plans
const activePlans = computed(() => {
  const plans = []
  if (hasBasicMethods.value) plans.push('basic')
  if (hasSilverMethods.value) plans.push('silver')
  if (hasGoldMethods.value) plans.push('gold')
  return plans
})

const validateForm = () => {
  let isValid = true

  if (teachingMethodsRef.value && !teachingMethodsRef.value.validate()) {
    isValid = false
  }

  return isValid
}

const saveDraft = async () => {
  loading.value = true
  errors.value = {}

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/services-offered', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        is_draft: true, // Indicate this is a draft save
        teaching_method_ids: selectedMethods.value,
        additional_services: selectedAdditionalServices.value,
        about_me: aboutMe.value || null
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save draft. Please try again.' }
      }
      loading.value = false
      return
    }

    loading.value = false
    errors.value = { success: 'Draft saved successfully!' }
    setTimeout(() => {
      errors.value = {}
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred while saving draft. Please try again.' }
    loading.value = false
  }
}

const submitForm = async () => {
  if (!validateForm()) {
    setTimeout(() => {
      const errorMessages = document.querySelectorAll('p.text-red-500, div.text-red-500')
      const firstVisibleError = Array.from(errorMessages).find(el =>
        el.offsetParent !== null && el.textContent.trim().length > 1
      )
      if (firstVisibleError) {
        firstVisibleError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    return
  }

  loading.value = true
  errors.value = {}

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/services-offered', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        teaching_method_ids: selectedMethods.value,
        additional_services: selectedAdditionalServices.value,
        about_me: aboutMe.value || null
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false

      // Scroll to the first visible error message
      setTimeout(() => {
        const errorMessages = document.querySelectorAll('p.text-red-500, div.text-red-500')
        const firstVisibleError = Array.from(errorMessages).find(el =>
          el.offsetParent !== null && el.textContent.trim().length > 1
        )
        if (firstVisibleError) {
          firstVisibleError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }

    loading.value = false
    trackEvent('tutorRegistrationEnd', { method: 'application_form' })
    emit('next', data)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}

const goBack = () => {
  emit('back')
}
</script>

<template>
  <div class="grid lg:grid-cols-3 gap-8">
    <!-- Main Content -->
    <div class="lg:col-span-2">
      <form @submit.prevent="submitForm" novalidate class="space-y-8">
        <!-- Success Message -->
        <div v-if="errors.success" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
          <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
            <span class="material-symbols-outlined text-white text-xl">check_circle</span>
          </div>
          <div>
            <p class="text-green-900 font-semibold">Success</p>
            <p class="text-green-700 text-sm">{{ errors.success }}</p>
          </div>
        </div>

        <!-- General Error Message -->
        <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
          <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
            <span class="material-symbols-outlined text-white text-xl">error</span>
          </div>
          <div>
            <p class="text-red-900 font-semibold">Error</p>
            <p class="text-red-700 text-sm">{{ errors.general }}</p>
          </div>
        </div>

        <!-- Intro Section -->
        <section id="intro-section">
          <h2 class="text-3xl font-bold text-gray-900 mb-3">What would you like to offer?</h2>
          <p class="text-lg text-gray-600">Select all the teaching methods you want to provide to your students. You can choose multiple options to create comprehensive learning experiences.</p>
        </section>

        <!-- Teaching Methods Selection Component -->
        <TeachingMethodsSelection
          ref="teachingMethodsRef"
          :methods="teachingMethods"
          :model-value="selectedMethods"
          @update:model-value="(val) => selectedMethods = val"
          title="Primary Teaching Methods"
          description="Select the teaching methods you want to offer"
          :required="true"
          :errors="errors"
          :columns="2"
        />

        <!-- Bio Section -->
        <section id="bio-section" class="md:bg-white md:rounded-2xl md:border-2 border-gray-100 p-0 md:p-8">
          <div class="flex items-start mb-6">
            <div class="hidden md:block flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-4 shadow-lg shadow-purple-200">
              <span class="material-symbols-outlined text-white text-2xl">person_edit</span>
            </div>
            <div>
              <h3 class="text-lg md:text-xl font-bold text-gray-900">Tell Students About Yourself</h3>
              <p class="text-gray-600 mt-1">Write a compelling bio that helps students understand who you are and what makes you a great tutor.</p>
            </div>
          </div>

          <div class="space-y-4 max-sm:p-8 max-sm:bg-white max-sm:rounded-2xl max-sm:border-2 border-gray-100">
            <div>
              <label for="about_me" class="block text-sm font-semibold text-gray-700 mb-2">
                Your Bio
                <span class="text-gray-400 font-normal">(Optional)</span>
              </label>
              <textarea
                id="about_me"
                v-model="aboutMe"
                rows="6"
                :maxlength="aboutMeMaxChars"
                class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all duration-200 resize-none"
                placeholder="Share your teaching philosophy, experience, and what makes your tutoring unique. Students love hearing about your background and approach to helping them succeed..."
              ></textarea>
              <div class="flex justify-between items-center mt-2">
                <p class="text-sm text-gray-500">
                  <span v-if="aboutMeCharCount > 0 && aboutMeCharCount < aboutMeMinChars" class="text-amber-600">
                    Minimum {{ aboutMeMinChars }} characters recommended ({{ aboutMeMinChars - aboutMeCharCount }} more needed)
                  </span>
                  <span v-else-if="aboutMeCharCount === 0" class="text-gray-400">
                    Write at least {{ aboutMeMinChars }} characters for best results
                  </span>
                  <span v-else class="text-green-600">
                    Great length!
                  </span>
                </p>
                <p class="text-sm" :class="aboutMeCharCount > aboutMeMaxChars * 0.9 ? 'text-amber-600' : 'text-gray-400'">
                  {{ aboutMeCharCount }}/{{ aboutMeMaxChars }}
                </p>
              </div>
              <p v-if="errors.about_me" class="text-red-500 text-sm mt-1">{{ errors.about_me }}</p>
            </div>

            <!-- Tips for writing a good bio -->
            <div class="bg-purple-50 rounded-xl p-4 mt-4">
              <h4 class="text-sm font-semibold text-purple-900 mb-2 flex items-center">
                <span class="material-symbols-outlined text-purple-600 text-lg mr-2">lightbulb</span>
                Tips for a Great Bio
              </h4>
              <ul class="text-sm text-purple-800 space-y-1">
                <li class="flex items-start">
                  <span class="text-purple-500 mr-2">•</span>
                  Mention your qualifications and relevant experience
                </li>
                <li class="flex items-start">
                  <span class="text-purple-500 mr-2">•</span>
                  Describe your teaching style and approach
                </li>
                <li class="flex items-start">
                  <span class="text-purple-500 mr-2">•</span>
                  Share what you enjoy about tutoring
                </li>
                <li class="flex items-start">
                  <span class="text-purple-500 mr-2">•</span>
                  Include any achievements or specializations
                </li>
              </ul>
            </div>
          </div>
        </section>

        <!-- Additional Services Section - Hidden for now -->
        <!-- <AdditionalServicesSection
          v-model="selectedAdditionalServices"
          :services="additionalServices"
        /> -->

        <!-- Testimonials Section -->
        <!-- <TestimonialsSection /> -->

      </form>
    </div>

    <!-- Right Sidebar - Only visible on large screens -->
    <aside id="sidebar-info" class="hidden lg:block lg:col-span-1 self-start sticky top-24">
      <!-- Tiered Pricing Info -->
      <PricingPlanSelector
        :active-plans="activePlans"
        :has-basic-methods="hasBasicMethods"
        :has-silver-methods="hasSilverMethods"
        :has-gold-methods="hasGoldMethods"
        class="mb-6"
      />

      <!-- Selection Summary -->
      <SelectionSummary
        :selected-methods="selectedMethods"
        :all-methods="teachingMethods"
        title="Your Selection"
        class="mb-6"
      />

      <!-- Pro Tips -->
      <ProTipsCard />

      <!-- Help Section -->
      <HelpSupportCard />
    </aside>
  </div>

  <!-- Navigation Buttons -->
  <NavigationButtons
    :loading="loading"
    :show-previous="true"
    primary-button-text="Continue to Pricing & Availability"
    primary-button-loading-text="Saving..."
    @previous="goBack"
    @save-draft="saveDraft"
    @submit="submitForm"
  />
</template>
